import React, { useState } from "react";
import { ethers } from "ethers";
import blockchainConfig from "../blockchain/config";
import abi from "../blockchain/ProvablyFairRNG.abi.json";

const Verify = ({ onClose }) => {
  const [inputSessionId, setInputSessionId] = useState("");
  const [participants, setParticipants] = useState([]);
  const [winners, setWinners] = useState([]);
  const [error, setError] = useState("");

  const handleQuery = async () => {
    if (!inputSessionId) {
      setError("Session ID cannot be empty.");
      return;
    }
    try {
      setError("");

      // Initialize provider using RPC URL from the config
      const provider = new ethers.JsonRpcProvider(blockchainConfig.rpcUrl);

      // Initialize contract using provider
      const contract = new ethers.Contract(
        blockchainConfig.contractAddress,
        abi,
        provider
      );

      // Query participants
      const fetchedParticipants = await contract.getParticipants(inputSessionId);
      setParticipants(fetchedParticipants);

      // Query winners
      const fetchedWinners = await contract.getWinners(inputSessionId);
      setWinners(fetchedWinners);
    } catch (err) {
      let friendlyMessage = "An error occurred while fetching results.";
      if (err.reason && err.reason.includes("Winners not selected yet")) {
        friendlyMessage =
          "Winners have not been selected for this session. Please try again later.";
      }
      setParticipants([]);
      setWinners([]);
      setError(friendlyMessage);
      console.error("Error details:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative w-full max-w-xl overflow-hidden bg-gray-900 shadow-xl rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-2xl font-bold text-indigo-400">
            Verify Giveaway Results
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            {/* Simple X icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
          <div className="space-y-3">
            <input
              type="text"
              value={inputSessionId}
              onChange={(e) => setInputSessionId(e.target.value)}
              placeholder="Enter Session ID"
              className="w-full px-4 py-3 text-white placeholder-gray-500 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
          <button
            onClick={handleQuery}
            className="w-full px-6 py-3 font-semibold text-white transition-colors bg-green-600 rounded-lg hover:bg-green-700"
          >
            Query Results
          </button>
          <div className="space-y-8">
            <div>
              <h3 className="mb-3 text-xl font-semibold text-indigo-300">Participants</h3>
              <div className="p-4 overflow-y-auto bg-gray-800 rounded-lg shadow-inner max-h-48">
                {participants.length > 0 ? (
                  <ul className="space-y-2">
                    {participants.map((participant, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between p-2 transition-colors bg-gray-700 rounded-lg hover:bg-gray-600"
                      >
                        <span className="font-mono text-sm text-gray-300 truncate" title={participant}>
                          {`${participant.slice(0, 16)}...${participant.slice(-4)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No participants found.</p>
                )}
              </div>
            </div>
            <div>
              <h3 className="mb-3 text-xl font-semibold text-indigo-300">Winners</h3>
              <div className="p-4 overflow-y-auto bg-gray-800 rounded-lg shadow-inner max-h-48">
                {winners.length > 0 ? (
                  <ul className="space-y-2">
                    {winners.map((winner, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between p-2 transition-colors bg-gray-700 rounded-lg hover:bg-gray-600"
                      >
                        <a
                          href={`https://explore.steamexchange.io/address/${winner}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-sm text-blue-400 truncate hover:underline"
                          title={winner}
                        >
                          {`${winner.slice(0, 16)}...${winner.slice(-4)}`}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No winners found.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Verify;
